import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const cartPairAbortControllers = new WeakMap();

/**
 * Initializes cart widgets that can be inserted by cart section morphs.
 * @param {ParentNode} root
 */
function initializeCartWidgets(root = document) {
  initializeCartPairItWith(root);
}

/**
 * Keeps cart drawer stateful classes/attributes in sync after cart section morphs.
 * Outer drawer nodes are not always replaced when the empty cart becomes filled.
 */
function updateCartDrawerAfterMorph() {
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (!(cartDrawer instanceof HTMLElement)) return;

  const dialog = cartDrawer.querySelector('.cart-drawer__dialog');
  if (!(dialog instanceof HTMLElement)) return;

  const hasCartForm = dialog.querySelector('.cart-form') !== null;
  dialog.classList.toggle('cart-drawer--empty', !hasCartForm);

  requestAnimationFrame(() => {
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!hasCartForm || !content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    dialog.setAttribute('cart-summary-sticky', summaryHeight / drawerHeight > 0.5 ? 'false' : 'true');
  });
}

function updateCartAfterMorph() {
  initializeCartWidgets(document);
  updateCartDrawerAfterMorph();
}

/**
 * Initializes cart "Pair it with" widgets after cart section morphs.
 * Inline scripts added through DOM morphing are not re-executed by the browser,
 * so this lives in the cart component module that is already loaded.
 * @param {ParentNode} root
 */
function initializeCartPairItWith(root = document) {
  for (const wrap of root.querySelectorAll('.cart-pair-wrap')) {
    if (!(wrap instanceof HTMLElement)) continue;

    cartPairAbortControllers.get(wrap)?.abort();

    const abortController = new AbortController();
    cartPairAbortControllers.set(wrap, abortController);
    const { signal } = abortController;

    const track = wrap.querySelector('.cart-pair-track');
    if (!(track instanceof HTMLElement)) continue;

    const prevBtn = wrap.querySelector('.cart-pair-nav-btn[id*="prev"]');
    const nextBtn = wrap.querySelector('.cart-pair-nav-btn[id*="next"]');
    const items = Array.from(track.querySelectorAll('[data-comp-item]')).filter(
      (item) => item instanceof HTMLElement
    );
    const total = items.length;
    let current = 0;

    const updateSlider = () => {
      track.style.transform = `translateX(-${current * 100}%)`;
      if (prevBtn instanceof HTMLButtonElement) prevBtn.disabled = current === 0;
      if (nextBtn instanceof HTMLButtonElement) nextBtn.disabled = current >= total - 1;
    };

    prevBtn?.addEventListener(
      'click',
      () => {
        if (current <= 0) return;
        current--;
        updateSlider();
      },
      { signal }
    );

    nextBtn?.addEventListener(
      'click',
      () => {
        if (current >= total - 1) return;
        current++;
        updateSlider();
      },
      { signal }
    );

    updateSlider();
    updateCartPairItems(wrap, items);

    wrap.addEventListener('click', handleCartPairAddToCart, { signal });
  }
}

/**
 * @param {HTMLElement} wrap
 * @param {HTMLElement[]} items
 */
function updateCartPairItems(wrap, items) {
  const selectedOptions = wrap.dataset.lastItemVariantTitle ? [wrap.dataset.lastItemVariantTitle] : [];

  for (const item of items) {
    const matchedId = findMatchingCartPairVariantId(item, selectedOptions);
    if (!matchedId) continue;

    const btn = item.querySelector('[data-comp-atc]');
    if (btn instanceof HTMLElement) btn.dataset.variantId = String(matchedId);

    const imageMap = parseCartPairMap(item.dataset.compImageMap);
    const imgEl = item.querySelector('[data-comp-image]');
    const newImgUrl = imageMap[String(matchedId)];
    if (imgEl instanceof HTMLImageElement && newImgUrl && imgEl.src !== newImgUrl) {
      imgEl.style.opacity = '0';
      imgEl.addEventListener('load', () => (imgEl.style.opacity = '1'), { once: true });
      imgEl.src = newImgUrl;
      if (imgEl.complete) imgEl.style.opacity = '1';
    }
  }
}


/**
 * @param {HTMLElement} item
 * @param {string[]} selectedOptions
 * @returns {string | undefined}
 */
function findMatchingCartPairVariantId(item, selectedOptions) {
  const variantMap = parseCartPairMap(item.dataset.compVariantMap);
  const option1Map = parseCartPairMap(item.dataset.compOption1Map);

  for (const optVal of selectedOptions) {
    if (variantMap[optVal]) return variantMap[optVal];
    if (option1Map[optVal]) return option1Map[optVal];
  }

  for (const optVal of selectedOptions) {
    const variantKey = Object.keys(variantMap).find((key) => key.includes(optVal) || optVal.includes(key));
    if (variantKey) return variantMap[variantKey];

    const optionKey = Object.keys(option1Map).find((key) => key.includes(optVal) || optVal.includes(key));
    if (optionKey) return option1Map[optionKey];
  }

  return item.dataset.fallbackVariantId;
}

/**
 * @param {string | undefined} value
 * @returns {Record<string, string>}
 */
function parseCartPairMap(value) {
  try {
    return JSON.parse(value || '{}');
  } catch {
    return {};
  }
}


/**
 * @param {Event} event
 */
function handleCartPairAddToCart(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const btn = target.closest('[data-comp-atc]');
  if (!(btn instanceof HTMLButtonElement)) return;

  const variantId = btn.dataset.variantId;
  if (!variantId || btn.disabled) return;

  btn.disabled = true;
  const originalText = btn.textContent?.trim() || 'Add';
  btn.textContent = '...';

  const sectionsToRender = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionsToRender.add(el.dataset.sectionId);
  });

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [{ id: parseInt(variantId, 10), quantity: 1 }],
      sections: Array.from(sectionsToRender).join(','),
      sections_url: window.location.pathname,
    }),
  })
    .then((response) => response.json())
    .then((cartData) => {
      btn.textContent = 'Added';
      btn.classList.add('is-added');
      setTimeout(() => {
        btn.textContent = originalText;
        btn.classList.remove('is-added');
        btn.disabled = false;
      }, 2000);

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cartData, sourceId: 'cart-pair', data: { sections: cartData.sections || {} } },
        })
      );

      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && 'open' in cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();
    })
    .catch(() => {
      btn.textContent = originalText;
      btn.disabled = false;
    });
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    initializeCartWidgets(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full', { injectStylesheet: true });
        updateCartAfterMorph();

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(updateCartAfterMorph);
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, 'full', { injectStylesheet: true });
      updateCartAfterMorph();

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(updateCartAfterMorph);
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
